import { NON_BREAKING_SPACE_CODE } from '@sdk/common/constants';

import config from '../config';

import emptyStateUrl from './empty-state.html';
class PageDetailCtrl {
  emptyStateUrl = emptyStateUrl;
  canBeMarkedAsSeen = {};
  newPost = {};
  today = new Date();
  loadingMore = false;
  page = null;
  showContent = false;
  articlesLimit = 5;
  _timeouts = [];

  constructor(
    $scope,
    $state,
    $window,
    $uibModal,
    $q,
    $timeout,
    Authentication,
    PersonaCollection,
    Restangular,
    NewsfeedCollection,
    NewsfeedReactBridge,
    Upload,
    Utils,
    ConfirmService,
    Mentions,
    AlertService,
    WindowLocation,
    dateFilter,
    orderByFilter
  ) {
    'ngInject';

    Object.assign(this, {
      $scope,
      $state,
      $window,
      $uibModal,
      $q,
      $timeout,
      Authentication,
      PersonaCollection,
      Restangular,
      NewsfeedCollection,
      NewsfeedReactBridge,
      Upload,
      Utils,
      ConfirmService,
      Mentions,
      AlertService,
      WindowLocation,
      dateFilter,
      orderByFilter,
    });
  }

  $onInit() {
    this.reactPageEnabled = config.REACT_NEWSFEED_PAGE_ENABLED;
    this.canCreatePages = this.NewsfeedCollection.canPageBeCreatedBy(this.Authentication.user);
    this.newPosts = this.NewsfeedCollection.newPosts;
    this.users = this.PersonaCollection.users;
    this.personas = this.PersonaCollection.personas;
    this.time = this.Utils.getDayPart();
    this.yourself = this.PersonaCollection.me;
    this.hasAccessToUser = this.PersonaCollection.hasAccessToUser;
    this.isTest = __TEST__;
    this.editingArticle = null;

    this.$scope.$on(
      '$destroy',
      this.NewsfeedCollection.onChange(() => this._refreshWritablePagesOptions)
    );
    this.initPages();
    this._refreshWritablePagesOptions();
    this._timeouts[0] = this.$timeout(() => {
      this.showContent = true;
      // delay content rendering so there's no lag when navigating to newsfeed
    }, 500);
  }

  $onDestroy() {
    this._timeouts.map((item) => this.$timeout.cancel(item));
  }

  async initPages() {
    await this._loadPage();
    await this.page.markAsRead();
  }

  get subscribedPages() {
    return this.NewsfeedCollection.subscribedPages;
  }

  get writablePages() {
    return this.NewsfeedCollection.pages.filter(
      (p) => this.NewsfeedCollection.canPostArticle(p, this.Authentication.user) && p.writable
    );
  }

  get allPages() {
    return this.NewsfeedCollection.pages;
  }

  get emptyStateType() {
    if (!this.page) {
      return null;
    }

    if (this.page.id === 0) {
      return `my-newsfeed-${this._getMyNewsfeedEmptySubtype()}`;
    }

    if (!this.page.articles.length) {
      return 'page-no-articles';
    }

    return null;
  }

  async _loadPage() {
    await this.NewsfeedCollection.loadData();
    const result = this.NewsfeedCollection.allChannels.find(
      (c) => String(c.id) === String(this.pageId)
    );
    if (result) {
      this.page = await this.NewsfeedCollection.getChannelDetails(result);
      this.newPostInitialValue = this.newPosts[this.page.id]?.newPost?.content ?? '';
      this.canPostArticle = this.NewsfeedCollection.canPostArticle(
        this.page,
        this.Authentication.user
      );
      this.employeeCanActOnPage = this.NewsfeedCollection.employeeCanActOnPage(
        this.page,
        this.Authentication.user
      );
      this.showPostForm = this._showPostForm();

      this.mentions = new this.Mentions(
        this.page.id === 0 ? this.PersonaCollection.users : this.page.members
      );
      this.loadBuffer();
      this._handleNewPosts();

      return;
    }

    this.$state.go('newsfeed.view.mynewsfeed');
  }

  _showPostForm() {
    if (!this.canPostArticle || !this.employeeCanActOnPage) {
      return false;
    }

    if (this.page.writable) {
      return true;
    }

    return !this.page.id && this.writablePages?.length;
  }

  loadBuffer() {
    if (this.page.articleBuffer?.length) {
      this.page.articles.push(...this.page.articleBuffer);
      this.page.articleBuffer = [];

      // Re-order the articles
      this.Restangular.restangularizeElement(null, this.page, 'channels');
    }
  }

  deleteArticle(article) {
    this.ConfirmService.ask(
      'Are you sure you want to delete this post?',
      article.remove
    ).ignoreCancel();
  }

  moveArticle(article) {
    this.NewsfeedCollection.moveArticle(article);
  }

  loadMore() {
    if (this.articlesLimit < this.page.articles.length) {
      this.articlesLimit = this.page.articles.length;
      return;
    }

    if (this.loadingMore) {
      return this.$q.resolve();
    }

    const lastPost = this.page.articles[this.page.articles.length - 1];

    this.loadingMore = true;
    return this.page
      .all('articles')
      .getList({ before: lastPost.id })
      .then((data) => {
        if (!data.length) {
          this.noMoreToLoad = true;
          return;
        }

        data.forEach((article) => {
          if (!this.page.articles.find((a) => a.id === article.id)) {
            this.page.articles.push(article);
          }
        });

        this.articlesLimit = this.page.articles.length;
        // Re-order the articles
        this.Restangular.restangularizeElement(null, this.page, 'channels');
      })
      .finally(() => {
        this.loadingMore = false;
      });
  }

  isFirstUnpinnedArticle(indexArg) {
    const index = Number(indexArg);
    const article = this.page.articles[index];
    const previousArticle = index ? this.page.articles[index - 1] : null;

    return !article.sticky && (!previousArticle || previousArticle.sticky);
  }

  showDateHeaderForRow(indexArg) {
    const index = Number(indexArg);

    if (isNaN(index)) {
      return false;
    }

    const article = this.page.articles[index];

    // pinned articles are acting as the regular ones on my newsfeed page
    if (article.sticky && this.page.id) {
      return false;
    }

    // Of course always show for the first article, no further check necessary
    if (index === 0) {
      return true;
    }

    const previousArticle = this.page.articles[index - 1];

    // If it's the first non-sticky article then we need to show the header always
    if (this.page.id !== 0 && previousArticle.sticky && !article.sticky) {
      return true;
    }

    return (
      this.dateFilter(previousArticle.posted, 'MMMM dd') !==
      this.dateFilter(article.posted, 'MMMM dd')
    );
  }

  submitArticleReact(text, attachments, pageId) {
    if (!this.newPosts[this.page.id].newPost) {
      this.newPosts[this.page.id].newPost = {};
    }

    this.newPosts[this.page.id].newPost.content = text;
    this.newPosts[this.page.id].newPost.attachments = attachments;
    if (pageId) {
      const postToPageInCollection = this.writablePages.find((p) => p.id === pageId);

      if (postToPageInCollection) {
        this.newPosts[this.page.id].postToPage = postToPageInCollection;
      }
    }

    this.submitArticle();
  }

  editArticle = (article) => {
    this.editingArticle = article;
  };

  submitArticleEdit = async (content, attachments, sticky) => {
    this.editingArticle.content = content;
    this.editingArticle.attachments = attachments;
    this.editingArticle.sticky = sticky;
    await this.editingArticle.put();

    this.editingArticle = null;
  };

  showErrorToast = (error) => {
    if (error) {
      this.AlertService.danger(error);
    }
  };

  onContentChange(text, attachments, pageId) {
    if (!this.newPosts[this.page.id].newPost) {
      this.newPosts[this.page.id].newPost = { attachments: [], content: '' };
    }

    if (typeof text === 'string') {
      this.newPosts[this.page.id].newPost.content = text !== NON_BREAKING_SPACE_CODE ? text : '';
    }

    if (attachments) {
      this.newPosts[this.page.id].newPost.attachments = attachments;
    }

    if (pageId) {
      const postToPageInCollection = this.writablePages.find((p) => p.id === pageId);

      if (postToPageInCollection) {
        this.newPosts[this.page.id].postToPage = postToPageInCollection;
      }
    }
  }

  submitArticle(form) {
    if (this.page.id > 0) {
      return this.page.addArticle(this.newPosts[this.page.id].newPost, form);
    }

    const { postToPage } = this.newPosts[this.page.id];
    return postToPage.addArticle(this.newPosts[this.page.id].newPost, form);
  }

  markArticleAsSeen(id) {
    if (this.canBeMarkedAsSeen[id]) {
      this.page.markAsSeen(this.page.articles.find((a) => a.id === id));
    }
  }

  onReveal = (id) => {
    this.canBeMarkedAsSeen[id] = true;
    this.markArticleAsSeen(id);
  };

  createPage() {
    this.$state.go('newsfeed.management.createPage');
  }

  removeUpload(index) {
    this.newPosts[this.page.id].newPost.attachments.splice(index, 1);
  }

  getPageMemberCount = (id) => {
    const page = this.NewsfeedCollection.allChannels.find((c) => String(c.id) === String(id));
    return page?.members?.length || 0;
  };

  _getMyNewsfeedEmptySubtype() {
    if (this.isEmptyNewsfeed) {
      return 'no-pages';
    }

    if (!this.subscribedPages.length && !this.NewsfeedCollection.searchQuery) {
      return 'no-subscribed-pages';
    }

    if (!this.page.articles.length) {
      return 'no-articles';
    }
  }

  _refreshWritablePagesOptions() {
    if (this.writablePages.length) {
      const sortedPages = this.orderByFilter(this.writablePages, 'name');
      this.writablePagesOptions = sortedPages?.map?.((p) => ({
        label: p.name,
        value: p,
      }));
      this.writablePagesReact = sortedPages;
    }
  }

  _handleNewPosts() {
    if (!(this.page.id in this.newPosts)) {
      this.newPosts[this.page.id] = {};
    }

    const newPostsToCurrentPage = this.newPosts[this.page.id];
    if (newPostsToCurrentPage.postToPage) {
      const postToPageInCollection = this.writablePages.find(
        (p) => p.id === newPostsToCurrentPage.postToPage.id
      );
      if (!postToPageInCollection) {
        delete newPostsToCurrentPage.postToPage;
      }
    }

    if (
      this.page.id === 0 &&
      this.writablePages?.length &&
      !this.newPosts[this.page.id].postToPage
    ) {
      this.newPosts[this.page.id].postToPage = this.writablePages[0];
    }
  }

  copyShareUrl = async (article) => {
    const path = `/newsfeed/page/${article.channel.id}/article/${article.id}`;
    const url = this.WindowLocation.origin + path;

    try {
      // This will work only on https:// !!
      await navigator.clipboard.writeText(url);
      this.AlertService.success('Link copied to clipboard');
    } catch (e) {
      this.AlertService.danger('Could not copy link.');
    }
  };
}

export default PageDetailCtrl;
